import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Dialog, DialogContent } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Search, Loader2, Clock, X } from 'lucide-react';
import { useWordPopover } from '../shared/reader/useWordPopover';
import WordPopover from '../shared/reader/WordPopover';
import {
  readRecentLookups,
  clearRecentLookups,
  RECENT_LOOKUPS_EVENT,
} from '../shared/reader/recentLookups';
import LanguageFlag, { LANG_META } from './LanguageFlag';
import { useAuth } from '../contexts/AuthContext';
import { vocabularyApi } from '../utils/api';
import { toast } from 'sonner';

/**
 * Command Palette — the same word popover, summoned from anywhere.
 *
 * Hotkeys: Ctrl+K (Win/Linux), Cmd+K (Mac).
 * On submit, we use useWordPopover.openStandalone() so the popover renders
 * inline inside the dialog body (no DOM anchor).
 *
 * Language defaults to the user's current studied language, pulled from
 * localStorage `current_language`; falls back to English.
 */
export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [lang, setLang] = useState(
    (typeof window !== 'undefined' && localStorage.getItem('current_language')) || 'en'
  );
  const { user } = useAuth();
  const [actionLoading, setActionLoading] = useState(false);  const nativeLang = useMemo(
    () =>
      typeof window !== 'undefined'
        ? (localStorage.getItem('locale') || 'en').split('-')[0].toLowerCase()
        : 'en',
    []
  );

  const popover = useWordPopover({
    languageCode: lang,
    interfaceLang: nativeLang,
  });

  // Recent lookups — refresh from localStorage when opened + on every push event
  const [recent, setRecent] = useState([]);
  useEffect(() => {
    if (!open) return;
    const refresh = () => setRecent(readRecentLookups());
    refresh();
    window.addEventListener(RECENT_LOOKUPS_EVENT, refresh);
    return () => window.removeEventListener(RECENT_LOOKUPS_EVENT, refresh);
  }, [open]);

  // ── Global hotkey ──
  useEffect(() => {
    if (!user) return;  // only active once authenticated
    const onKey = (e) => {
      const isMac = /Mac|iPhone|iPad|iPod/.test(navigator.platform);
      const modifier = isMac ? e.metaKey : e.ctrlKey;
      if (modifier && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setOpen(true);
      } else if (e.key === 'Escape' && open) {
        handleClose();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, user]);

  const handleClose = () => {
    popover.close();
    setQuery('');
    setOpen(false);
  };

  const handleSearch = (e) => {
    e?.preventDefault?.();
    const raw = query.trim();
    if (!raw) return;
    // Blur the input so the X/R/F/K shortcuts defined in <WordPopover> fire
    // on `document.keydown` instead of being captured as text in the Input.
    if (typeof document !== 'undefined') {
      const active = document.activeElement;
      if (active && typeof active.blur === 'function') active.blur();
    }
    popover.openStandalone(raw);
  };

  // ── Popover action wiring (same shape as Reader/VideoReader) ──
  const handleSetStatus = useCallback(
    async (status) => {
      const term = popover.selectedToken?.normalized_form;
      if (!term) return;
      setActionLoading(true);
      try {
        await vocabularyApi.setWordStatus(term, lang, status);
        toast.success(`Marked as ${status}`);
        popover.close();
        setQuery('');
      } catch {
        toast.error('Could not update status');
      } finally {
        setActionLoading(false);
      }
    },
    [popover, lang]
  );

  const handleSaveTranslation = useCallback(async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      let stateId = popover.wordData?.user_state?.state_id;
      if (!stateId) {
        await vocabularyApi.setWordStatus(term, lang, 'new');
        const res = await vocabularyApi.getWordData(term, lang);
        stateId = res.data?.user_state?.state_id;
      }
      if (stateId) {
        await vocabularyApi.addTranslation(stateId, popover.translation);
        popover.setTranslationSaved(true);
        toast.success('Translation saved');
      }
    } catch {
      toast.error('Could not save translation');
    } finally {
      setActionLoading(false);
    }
  }, [popover, lang]);

  const handleSaveNote = useCallback(async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      await vocabularyApi.saveUserNote(term, lang, popover.note);
      toast.success('Note saved');
    } catch {
      toast.error('Could not save note');
    } finally {
      setActionLoading(false);
    }
  }, [popover, lang]);

  const handleAutoTranslate = useCallback(async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    setActionLoading(true);
    try {
      const res = await vocabularyApi.enrichWord(term, lang, 'translation', true);
      const tr = res.data?.data?.translation || res.data?.translation;
      if (tr) {
        popover.setTranslation(tr);
        popover.setTranslationSaved(false);
      }
    } catch {
      toast.error('Translation failed');
    } finally {
      setActionLoading(false);
    }
  }, [popover, lang]);

  const handleRefreshSection = useCallback(
    async (section) => {
      const term = popover.selectedToken?.normalized_form;
      if (!term) return;
      setActionLoading(true);
      try {
        const res = await vocabularyApi.enrichWord(term, lang, section, true);
        if (res.data?.data) {
          popover.setWordData((prev) => (prev ? { ...prev, ...res.data.data } : res.data.data));
        }
      } catch {
        toast.error('Refresh failed');
      } finally {
        setActionLoading(false);
      }
    },
    [popover, lang]
  );

  const handlePlayAudio = useCallback(async () => {
    const term = popover.selectedToken?.normalized_form;
    if (!term) return;
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const audio = new Audio(
        `${BACKEND_URL}/api/tts/word?word=${encodeURIComponent(term)}&language=${lang}`
      );
      await audio.play();
    } catch {
      toast.error('Audio unavailable');
    }
  }, [popover, lang]);

  const LANG_OPTIONS = Object.entries(LANG_META).map(([code, m]) => ({ code, name: m.name }));

  // Guard rendering (not hook calls) on auth
  if (!user) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => (v ? setOpen(true) : handleClose())}>
      <DialogContent
        className="max-w-2xl p-0 overflow-hidden"
        data-testid="command-palette"
        aria-describedby={undefined}
      >
        <div className="p-4 border-b">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Quick lookup… (any word)"
                className="pl-9"
                data-testid="command-palette-input"
              />
            </div>
            <Select value={lang} onValueChange={setLang}>
              <SelectTrigger className="w-36" data-testid="command-palette-lang">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANG_OPTIONS.map((l) => (
                  <SelectItem key={l.code} value={l.code}>
                    <span className="flex items-center gap-2">
                      <LanguageFlag code={l.code} size="sm" />
                      {l.name}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button type="submit" disabled={!query.trim() || popover.wordLoading} data-testid="command-palette-submit">
              {popover.wordLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Look up'}
            </Button>
          </form>
          <p className="text-[10px] text-muted-foreground mt-2 tracking-wide">
            Hit <kbd className="px-1 py-0.5 rounded bg-muted font-mono text-[10px]">Esc</kbd> to close ·
            Open anywhere with <kbd className="px-1 py-0.5 rounded bg-muted font-mono text-[10px]">Ctrl / ⌘ K</kbd>
          </p>
        </div>

        {/* Recent lookups — shown only when nothing is being looked up yet */}
        {!popover.popoverPos && recent.length > 0 && (
          <div className="px-4 py-3 border-b" data-testid="command-palette-recent">
            <div className="flex items-center justify-between mb-2">
              <span className="inline-flex items-center gap-1.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">
                <Clock className="h-3 w-3" /> Recent
              </span>
              <button
                onClick={() => { clearRecentLookups(); setRecent([]); }}
                className="text-[10px] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"
                data-testid="command-palette-clear-recent"
                title="Clear recent lookups"
              >
                <X className="h-3 w-3" /> Clear
              </button>
            </div>
            <div className="flex flex-wrap gap-1.5">
              {recent.slice(0, 12).map((r, i) => (
                <button
                  key={`${r.term}-${r.lang}-${i}`}
                  onClick={() => {
                    setLang(r.lang);
                    setQuery(r.display || r.term);
                    // Blur so X/R/F/K shortcuts fire on the popover
                    if (typeof document !== 'undefined') {
                      const active = document.activeElement;
                      if (active && typeof active.blur === 'function') active.blur();
                    }
                    popover.openStandalone(r.display || r.term);
                  }}
                  className="inline-flex items-center gap-1.5 px-2 py-1 rounded-full text-xs bg-muted hover:bg-muted/70 border border-border/60 hover:border-primary/40 transition-colors"
                  data-testid={`recent-chip-${i}`}
                >
                  <LanguageFlag code={r.lang} size="sm" />
                  <span className="font-medium">{r.display || r.term}</span>
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Inline popover — only rendered after search */}
        {popover.popoverPos && (
          <div
            className="px-4 py-4 overflow-y-auto"
            style={{ maxHeight: '75vh' }}
            data-testid="command-palette-popover-container"
          >
            <WordPopover
              style={{ position: 'relative', width: '100%', maxHeight: 'none' }}
              wordData={popover.wordData}
              wordLoading={popover.wordLoading}
              token={popover.selectedToken}
              language={{ code: lang }}
              noteText={popover.note}
              setNoteText={popover.setNote}
              translationText={popover.translation}
              setTranslationText={popover.setTranslation}
              translationSaved={popover.translationSaved}
              onPlayAudio={handlePlayAudio}
              onSetStatus={handleSetStatus}
              onRelatedWordClick={(term) => popover.openForTerm(term)}
              onPopoverBack={() => popover.back()}
              historyDepth={popover.historyDepth}
              onSaveTranslation={handleSaveTranslation}
              onAutoTranslate={handleAutoTranslate}
              onSaveNote={handleSaveNote}
              onRefreshSection={handleRefreshSection}
              onClose={() => { popover.close(); setQuery(''); }}
              actionLoading={actionLoading}
              locked={popover.wordData?.locked}
              isMobile={false}
              isAdmin={user?.is_admin}
              isExpression={popover.isExpression}
              hasCachedAudio={popover.hasCachedAudio}
              isNewEntry={popover.isNewEntry}
            />
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
